import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { FaTimes } from 'react-icons/fa';
import styles from './ThemePanel.module.css';
import pageStyles from '../../../pages/SingleListingGenerator/SingleListingGeneratorPage.module.css';
import AuthContext from '../../../context/AuthContext';
import { useFormStep } from '../../../context/FormStepContext';
import { THEME_CONFIG } from '../../../pages/SingleListingGenerator/constants';
import themeThumbnailUrls from '../../../assets/theme_thumbnails_urls.json';
import themeSlideCounts from '../../../assets/theme_slide_counts.json';


// Number of slides to render for a given type. Prefers the slide-count JSON,
// falls back to THEME_CONFIG length, then to a sensible default.
const slideCount = (themeId, type) => {
  const counts = themeSlideCounts[themeId];
  if (counts && counts[type] != null) return counts[type];
  const cfg = THEME_CONFIG[themeId];
  if (cfg && Array.isArray(cfg[type])) return cfg[type].length;
  return type === 'aplus' ? 5 : 6;
};

const isNewFormat = (entry) => !!(entry && (entry.feature || entry.main || entry.aplus));

const loadThemeImage = (themeId, type, number) => {
  const urls = themeThumbnailUrls[themeId]?.[`${type}_thumbnails`];
  if (!urls) return null;
  // Newer zoomshot/lifestyle thumbnails are named `${themeId}_template${number}.png`
  // and don't follow the `${type}_${themeId}.${number}.` convention. Prefer the
  // alternate when it exists (only present for the relevant slots).
  const alternate = `${themeId}_template${number}.`;
  const primary = `${type}_${themeId}.${number}.`;
  return urls.find(u => u.includes(alternate))
      || urls.find(u => u.includes(primary))
      || null;
};

const ThemePanel = ({ isOpen, onClose, selectedTheme, onThemeSelect, isLoading, showBothSidebars = false, isBulk = false }) => {
  const authCtx = useContext(AuthContext);
  const { applyThemeToAll, setApplyThemeToAll } = useFormStep();
  const [themes, setThemes] = useState([]);
  const [loadingThemes, setLoadingThemes] = useState(true);
  const [error, setError] = useState(null);
  const [previewThemeId, setPreviewThemeId] = useState(null);
  // const [shuffleKey, setShuffleKey] = useState(0);

  // Build preview images for a theme/type from the real thumbnail set.
  // With THEME_CONFIG, slide numbers come from the config; otherwise we
  // emit all available thumbnails in order.
  const buildPreviewLegacy = (themeId, type) => {
    const urls = themeThumbnailUrls[themeId]?.[`${type}_thumbnails`];
    if (!urls || !urls.length) return [];
    const config = THEME_CONFIG[themeId];
    if (!config) {
      return urls.map((src, i) => ({ src, type, number: i + 1 }));
    }
    const skipFirst = type === 'bplus' && (themeId === 'theme9' || themeId === 'theme10');
    const numbers = skipFirst ? config[type].slice(1) : config[type];
    return numbers
      .map((num) => {
        const src = loadThemeImage(themeId, type, num);
        return src ? { src, type, number: num } : null;
      })
      .filter(Boolean);
  };

  const getBPlusImages = (themeId) => {
    const entry = themeThumbnailUrls[themeId];
    if (!isNewFormat(entry)) return buildPreviewLegacy(themeId, 'bplus');
    const list = [];
    if (entry.main) list.push({ src: entry.main, type: 'bplus', label: 'Main' });
    (entry.feature || []).forEach((src, i) =>
      list.push({ src, type: 'bplus', label: `Feature ${i + 1}` })
    );
    if (entry.zoomshot) list.push({ src: entry.zoomshot, type: 'bplus', label: 'Zoom' });
    if (entry.lifestyle) list.push({ src: entry.lifestyle, type: 'bplus', label: 'Lifestyle' });
    if (entry.application) list.push({ src: entry.application, type: 'bplus', label: 'Application' });
    if (entry.dim) list.push({ src: entry.dim, type: 'bplus', label: 'Dim' });
    return list;
  };

  const getAPlusImages = (themeId) => {
    const entry = themeThumbnailUrls[themeId];
    if (!isNewFormat(entry)) return buildPreviewLegacy(themeId, 'aplus');
    return (entry.aplus || []).map((src, i) => ({ src, type: 'aplus', number: i + 1 }));
  };

  // Track the in-flight request so we can abort it on close/remount and
  // ignore stale responses if a newer fetch supersedes the current one.
  const fetchAbortRef = useRef(null);

  const fetchThemes = useCallback(async () => {
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    setError(null);
    setLoadingThemes(true);
    try {
      const token = authCtx?.token || localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Not signed in');
      }
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/single-gen/themes`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal,
      });
      if (response.status === 401) {
        throw new Error('Session expired — please sign in again');
      }

      if (!response.ok) {
        throw new Error('Failed to fetch themes');
      }

      // Guard against dev-server SPA fallbacks (index.html served at 200) so
      // we surface a clear error instead of a confusing JSON parse failure.
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Themes endpoint returned a non-JSON response — check REACT_APP_API_URL and that the backend is running');
      }

      const data = await response.json();
      if (controller.signal.aborted) return;
      if (data.success && data.themes) {
        setThemes(data.themes);
      } else {
        throw new Error(data.error || 'Failed to load themes');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message);
    } finally {
      if (!controller.signal.aborted) {
        setLoadingThemes(false);
      }
    }
  }, [authCtx?.token]);

  // Refetch every time the panel transitions into "open". Using a ref to
  // detect the false→true edge prevents React from skipping the effect when
  // an unrelated re-render keeps isOpen at the same value.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      fetchThemes();
    }
    wasOpenRef.current = isOpen;
    if (!isOpen) {
      fetchAbortRef.current?.abort();
    }
  }, [isOpen, fetchThemes]);

  // useEffect(() => {
  //   if (isOpen) setShuffleKey((k) => k + 1);
  // }, [isOpen]);

  const handleThemeClick = (theme) => {
    if (!theme) return;
    // Show preview instead of selecting
    setPreviewThemeId(theme.id);
  };

  const handleSelectTheme = (theme) => {
    if (onThemeSelect) {
      onThemeSelect(theme);
    }
  };

  const getThemeThumbnail = (themeId) => {
    const entry = themeThumbnailUrls[themeId];
    if (isNewFormat(entry)) {
      return entry.aplus?.[0] || entry.main || null;
    }
    const aplusList = entry?.aplus_thumbnails;
    if (!aplusList || !aplusList.length) return null;
    const config = THEME_CONFIG[themeId];
    const firstAplusNumber = config?.aplus?.[0];
    if (firstAplusNumber != null) {
      const found = loadThemeImage(themeId, 'aplus', firstAplusNumber);
      if (found) return found;
    }
    return aplusList[0];
  };

  const renderThemeButton = (themeOption) => {
    if (!themeOption) return null;
    
    const themeId = themeOption.id;
    const isSelected = selectedTheme?.id === themeOption.id;
    const isOtherSelected = selectedTheme && !isSelected;
    const thumbnail = getThemeThumbnail(themeId);
    
    return (
      <button
        key={themeId}
        type="button"
        data-tutorial-theme-card={themeId}
        className={`${pageStyles.themeOneButton} ${isSelected ? pageStyles.themeOneButtonSelected : ''} ${isOtherSelected ? pageStyles.themeOneButtonOpaque : ''}`}
        onClick={() => handleThemeClick(themeOption)}
        disabled={isLoading}
      >
        <div className={pageStyles.themeCardContainer}>
          <div className={pageStyles.themeCardLayer1}></div>
          <div className={pageStyles.themeCardLayer2}></div>
          <div className={pageStyles.themeCardMain}>
            <img 
              src={thumbnail} 
              alt={themeOption.name || `Theme ${displayNumber[themeId] ?? themeId.replace('theme', '')}`}
              className={pageStyles.themeThumbnail}
            />
          </div>
        </div>
      </button>
    );
  };

  // Order known themes first, then any additional ones
  const orderedIds = [
    'theme1', 'theme2', 'theme3', 'theme4', 'theme5', 'theme6', 'theme7', 'theme8', 'theme9', 'theme10',
    'theme11', 'theme12', 'theme13', 'theme14', 'theme15', 'theme16', 'theme17', 'theme18', 'theme19', 'theme20',
    'theme21', 'theme22', 'theme23', 'theme24', 'theme25', 'theme26', 'theme27', 'theme28', 'theme29', 'theme30',
    'theme31', 'theme32', 'theme33', 'theme34', 'theme35', 'theme36', 'theme37', 'theme38', 'theme39',
    'theme40', 'theme41', 'theme42'
  ];
  const orderedThemes = orderedIds
    .map(id => themes.find(theme => theme.id === id))
    .filter(Boolean);
  const extraThemes = themes.filter(theme => !orderedIds.includes(theme.id));
  const ordered = [...orderedThemes, ...extraThemes];
  const allThemes = [...ordered].reverse();

  // Display numbers: re-number visible themes 1..N in chronological order so
  // hidden themes (commented out on the backend) don't leave gaps in labels.
  const displayNumber = Object.fromEntries(ordered.map((t, i) => [t.id, i + 1]));

  if (!isOpen) return null;

  return (
    <div className={styles.themePanelOverlay}>
      <div className={`${styles.themePanel} ${showBothSidebars ? styles.themePanelShifted : ''}`}> 
        <div className={styles.themePanelHeader}>
          <h2 className={styles.themePanelTitle}>Select Theme Templates</h2>
          <button
            className={styles.themePanelClose}
            onClick={onClose}
            aria-label="Close themes panel"
          >
            <FaTimes />
          </button>
        </div>

        {isBulk && (
          <label className={styles.applyToAllLabel}>
            <input
              type="checkbox"
              checked={applyThemeToAll}
              onChange={(e) => setApplyThemeToAll(e.target.checked)}
              className={styles.applyToAllCheckbox}
            />
            Apply same to all Products
          </label>
        )}

        <div className={styles.themePanelContent}>
          {loadingThemes ? (
            <p className={styles.loadingText}>Loading themes...</p>
          ) : error ? (
            <p className={styles.errorText}>Error loading themes: {error}</p>
          ) : previewThemeId ? (
            <>
              {/* Preview Mode */}
              <div className={styles.previewHeader}> 
                <button
                  type="button"
                  className={styles.backButton}
                  onClick={() => setPreviewThemeId(null)}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 12L6 8L10 4" stroke="#505050" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <h3 className={styles.previewTitle}>
                  {allThemes.find(t => t.id === previewThemeId)?.name
                    || `Theme ${displayNumber[previewThemeId] ?? previewThemeId.replace('theme', '')}`}
                </h3>
              </div>
              <button
                type="button"
                data-tutorial-select-theme="true"
                className={`${styles.selectThemeButton} ${selectedTheme?.id === previewThemeId ? styles.selectThemeButtonSelected : ''}`}
                onClick={() => { 
                  const theme = allThemes.find(t => t.id === previewThemeId); 
                  if (theme) { 
                    if (selectedTheme?.id === previewThemeId) { 
                      // Deselect the theme 
                      handleSelectTheme(null); 
                    } else { 
                      // Select the theme - stay on preview page 
                      handleSelectTheme(theme); 
                    } 
                  } 
                }}
                disabled={isLoading}
              > 
                {selectedTheme?.id === previewThemeId ? 'Selected' : 'Select this Theme'}
              </button>
              {/* B+ Templates Section */}
              <div className={styles.previewSection}> 
                <h4 className={styles.previewSectionTitle}>B+ Templates</h4>
                <div className={styles.previewGrid}> 
                  {getBPlusImages(previewThemeId).map((image, index) => (
                    <div key={`bplus-${index}`} className={styles.previewCard}> 
                      <img  
                        src={image.src}  
                        alt={`B+ Template ${index + 1}`}
                        className={styles.previewImage} 
                      />
                    </div>
                  ))}
                </div>
              </div>
              {/* A+ Templates Section */}
              <div className={styles.previewSection}> 
                <h4 className={styles.previewSectionTitle}>A+ Templates</h4>
                <div className={styles.previewGridSingle}> 
                  {getAPlusImages(previewThemeId).map((image, index) => (
                    <div key={`aplus-${index}`} className={styles.previewCard}> 
                      <img  
                        src={image.src}  
                        alt={`A+ Template ${index + 1}`}
                        className={styles.previewImage} 
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Theme Selection Mode */}
              <p className={styles.themePanelDescription}>Select a theme from below</p>
              <div className={styles.themeGrid}> 
                {allThemes.map(theme => renderThemeButton(theme))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThemePanel;
